import { useEffect, useState } from "react";
import { fetchAuditLog, type AuditEntry } from "@/lib/audit";

/** QC-manager audit trail viewer (Task 6). Filter by action + CSV export. */
export default function AuditLogModal({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    void (async () => {
      setRows(await fetchAuditLog());
      setLoading(false);
    })();
  }, []);

  const actions = Array.from(new Set(rows.map((r) => r.action))).sort();
  const shown = filter ? rows.filter((r) => r.action === filter) : rows;

  function exportPdf() {
    const esc = (s: unknown) =>
      String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] as string));
    const rows = shown
      .map(
        (r) => `<tr><td>${esc(new Date(r.created_at).toLocaleString())}</td><td>${esc(r.actor_name || "—")}</td>
        <td>${esc(r.action)}</td><td>${esc(r.entity_type || "")}${r.entity_id ? " · " + esc(r.entity_id) : ""}</td></tr>`,
      )
      .join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Audit trail</title>
      <style>@page{size:A4 portrait;margin:14mm}body{font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;color:#13212E;font-size:10.5px;padding:18px}
      h1{font-family:Georgia,serif;font-size:16px;border-bottom:3px solid #2C9B73;padding-bottom:8px}
      table{width:100%;border-collapse:collapse}th{background:#F5F6F8;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:.5px;color:#5A6B7A;padding:6px;border-bottom:1.5px solid #13212E}
      td{padding:5px 6px;border-bottom:1px solid #E8ECEF;vertical-align:top}.actions{position:fixed;top:12px;right:12px}.actions button{padding:8px 16px;background:#2C9B73;color:#fff;border:none;border-radius:3px;cursor:pointer}@media print{.noprint{display:none}}</style></head>
      <body><div class="actions noprint"><button onclick="window.print()">Save as PDF / Print</button></div>
      <h1>GridProof — Audit trail (${shown.length} entries)</h1>
      <table><thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Entity</th></tr></thead><tbody>${rows}</tbody></table>
      </body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  }

  function exportCsv() {
    const head = ["time", "actor", "action", "entity_type", "entity_id"];
    const body = shown.map((r) =>
      [r.created_at, r.actor_name || "", r.action, r.entity_type || "", r.entity_id || ""]
        .map((c) => `"${String(c).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[head.join(","), ...body].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "audit-log.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="gp-modal-overlay" onClick={onClose}>
      <div className="audit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gp-modal-head">
          <b>Audit trail</b>
          <button className="gp-modal-x" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="audit-toolbar">
          <select className="gp-input" style={{ maxWidth: 220 }} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All actions ({rows.length})</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={exportPdf} disabled={!shown.length}>
            ⧉ PDF
          </button>
          <button className="btn" onClick={exportCsv} disabled={!shown.length}>
            ⬇ CSV
          </button>
        </div>
        <div className="audit-list">
          {loading ? (
            <div className="audit-empty">Loading…</div>
          ) : shown.length === 0 ? (
            <div className="audit-empty">No audit entries yet. Actions appear here as they happen.</div>
          ) : (
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Entity</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{new Date(r.created_at).toLocaleString()}</td>
                    <td>{r.actor_name || "—"}</td>
                    <td>
                      <span className="audit-tag">{r.action}</span>
                    </td>
                    <td className="audit-entity">
                      {r.entity_type ? `${r.entity_type}` : ""}
                      {r.entity_id ? ` · ${r.entity_id}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <style>{AUDIT_CSS}</style>
    </div>
  );
}

const AUDIT_CSS = `
.audit-modal{ width:760px; max-width:94vw; max-height:82vh; display:flex; flex-direction:column;
  background:var(--panel,#0e1620); border:1px solid var(--line); border-radius:14px; padding:18px;
  box-shadow:0 24px 64px rgba(0,0,0,.5); }
.audit-toolbar{ display:flex; align-items:center; gap:10px; margin-bottom:12px; }
.audit-list{ overflow:auto; border:1px solid var(--line); border-radius:10px; }
.audit-empty{ padding:30px; text-align:center; color:var(--dim); font-size:13px; }
.audit-table{ width:100%; border-collapse:collapse; font-size:12.5px; }
.audit-table th{ text-align:left; padding:8px 10px; color:var(--dim); font-weight:600; font-size:10px;
  text-transform:uppercase; letter-spacing:.06em; position:sticky; top:0; background:var(--panel,#0e1620); }
.audit-table td{ padding:8px 10px; border-top:1px solid var(--line); vertical-align:top; }
.audit-tag{ font-family:ui-monospace,monospace; font-size:11px; color:var(--steel,#3A719E); }
.audit-entity{ color:var(--dim); }
`;
